import { Dispatch, useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import {
  CryptocurrenciesEnum,
  CurrencyEnum,
  CurrencySymbolEnum,
  LinkedAccountStatusEnum,
} from '../../../lib/enums';
import lang from '../../../lib/language';
import { WithdrawalRequest } from '../../../lib/models/crm/types';
import { AccountInfo, LinkedAccount } from '../../../lib/models/linked-account/types';
import { RootState } from '../../../lib/store';
import { getAccountsAvailableCash } from '../../../lib/store/common-actions';
import { getCurrentAccountTradingPlan } from '../../../lib/store/crm/helpers';
import { getCryptoRate, registerCryptoWithdrawalRequest } from '../../../lib/store/payment';
import { isActiveAccount } from '../../../lib/store/payment/helpers';
import { GetAllAvailableCashPayload } from '../../../lib/store/reporting/types';
import { renderWalletName } from '../../../lib/util/CryptoHelpers';
import {
  fixToFloat,
  formatCurrency,
  formatCurrencyBySymbol,
  formatNumber,
} from '../../../lib/util/DataHelpers';
import { ZERO_VALUE } from '../../../lib/util/types';

import iconAddNew from '../../../assets/img/icon-add_new.svg';
import InputErrorIcon from '../../../assets/img/input-error-icon.svg';
import config from '../../../config';
import { useBrowserWidth, useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import CommonDropdown, { CommonDropdownOption } from '../../../shared_components/CommonDropdown/CommonDropdown';
import { UI_ROUTES } from '../../../util/routes';
import { BaseModal, ClickableDiv, MobileDrawer } from '../..';
import ContainerBackButton from '../../ContainerBackButton/ContainerBackButton';
import GoBackButton from '../../GoBackButton/GoBackButton';
import LogoAndCloseButton from '../../LogoAndCloseButton/LogoAndCloseButton';
import { useWithdrawButton } from '../../Payouts/PayoutsHeader/useWithdrawButton';

import Modal from './modal/Modal';

import styles from './WithdrawCryptoAmount.module.scss';

const getPreselectedWallet = (accounts: LinkedAccount[]): LinkedAccount | undefined => {
  const newest = accounts.slice().reverse();
  return (
    newest.find((a) => a.status === LinkedAccountStatusEnum.Active)
    ?? newest.find((a) => a.status === LinkedAccountStatusEnum.New)
  );
};

const WithdrawCryptoAmount = (props: Props) => {
  const {
    tradingAccounts,
    cryptoLinkedAccounts,
    availableCash,
    payoutSplit,
    accountStatusData,
    currentPlan,
    buyingPower,
    referenceIds,
    getAvailablePayout,
    sendWithdrawalRequest,
    fetchCryptoRate,
    cryptoRate,
  } = props;

  const { totalRealizedPnl } = accountStatusData;

  const { potentialPayout } = useWithdrawButton({
    currentPlan,
    availableCash,
    payoutSplit,
    totalRealizedPnl,
    isGettingFirstAndLastTrade: false,
    hasOlderTradeThanFourteenDays: true,
  });

  const calculatedCash = potentialPayout;
  const minimumCryptoWithdrawal = (buyingPower ?? 0) * 0.01;

  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const navigate = useNavigate();
  const isDefaultGoBackButton = browserWidthSize >= 989 && !isMobile;

  const [isOpenCryptoWallets, setIsOpenCryptoWallets] = useState(false);
  const [openPopoverInfo, setOpenPopoverInfo] = useState(false);
  const [isShowNoActiveAccountModal, setIsShowNoActiveAccountModal] = useState(false);

  const preselectedWallet = getPreselectedWallet(cryptoLinkedAccounts);

  const [dataValues, setDataValues] = useState({
    selectedAccount: preselectedWallet,
    walletAddress: preselectedWallet?.account_info.crypto_wallet_address ?? '',
    amountValue: '',
  });

  useEffect(() => {
    if (referenceIds?.length > 0) {
      getAvailablePayout({ accountReferenceIds: referenceIds });
    }
    fetchCryptoRate();
  }, []);

  useEffect(() => {
    if (cryptoLinkedAccounts.length > 0 && !dataValues.selectedAccount) {
      const preselected = getPreselectedWallet(cryptoLinkedAccounts);
      if (preselected) {
        setDataValues((prev) => ({
          ...prev,
          selectedAccount: preselected,
          walletAddress: preselected.account_info.crypto_wallet_address ?? '',
        }));
      }
    }
  }, [cryptoLinkedAccounts]);

  const handleClickWallet = (account: LinkedAccount | null) => {
    if (!account) {
      setIsOpenCryptoWallets(false);
      return;
    }
    setDataValues((prev) => ({
      ...prev,
      selectedAccount: account,
      walletAddress: account.account_info.crypto_wallet_address,
    }));
    setIsOpenCryptoWallets(false);
  };

  const cryptoWalletOptions = cryptoLinkedAccounts.map((account) => ({
    id: String(account.id),
    label: renderWalletName(account.account_info.crypto_wallet_address),
    badge: account.status === LinkedAccountStatusEnum.New
      ? lang.commonVerificationStepStatusPending()
      : undefined,
    badgeClassName: styles.pendingBadge,
  }));

  const handleSelectCryptoWallet = (option: CommonDropdownOption) => {
    handleClickWallet(cryptoLinkedAccounts.find(
      (account) => String(account.id) === option.id,
    ) ?? null);
  };

  const handleAddCryptoWallet = () => {
    navigate(UI_ROUTES.payouts + UI_ROUTES.addCryptoAddress, {
      state: {
        previousPage: UI_ROUTES.payouts + UI_ROUTES.withdrawCryptoAmount,
      },
    });
  };

  const cryptoWalletsFooter = (
    <ClickableDiv className={styles.addCryptoWallet} onClick={handleAddCryptoWallet}>
      <img src={iconAddNew} alt="icon small plus" />
      <span className={styles.addCryptoWalletText}>
        {lang.mobileFundsScreenAddCryptoAccModalBtn()}
      </span>
    </ClickableDiv>
  );

  const handleRequestWithdrawal = () => {
    const activeTradingAccount = tradingAccounts.find(isActiveAccount);
    if (!activeTradingAccount) {
      setIsShowNoActiveAccountModal(true);
      return;
    }
    localStorage.setItem('cryptoWithdrawalAmount', dataValues.amountValue);
    sendWithdrawalRequest({
      amount: Number(dataValues.amountValue),
      bank_details: { crypto_wallet_address: dataValues.walletAddress } as AccountInfo,
    });
    navigate(UI_ROUTES.payouts, { state: { showCryptoWithdrawalModal: true } });
  };

  const { walletAddress } = dataValues;

  const isBelowMinimum = !!dataValues.amountValue
    && Number(dataValues.amountValue) < fixToFloat(minimumCryptoWithdrawal);
  const isAboveMaximum = Number(dataValues.amountValue) > fixToFloat(calculatedCash);
  const hasAmountError = isBelowMinimum || isAboveMaximum;

  const renderAvailableAmount = () => (
    <div className={styles.availableCashErrorWrapper}>
      {hasAmountError && <img src={InputErrorIcon} alt="Error icon" className={styles.errorIcon} />}
      <span
        className={classNames(styles.textAvailableCash, {
          [styles.textAvailableCashError]: hasAmountError,
        })}
      >
        {isBelowMinimum
          ? lang.minimumCryptoAmount(
            formatCurrencyBySymbol(minimumCryptoWithdrawal, CurrencySymbolEnum.USD),
          )
          : `${lang.availableCash()}: ${formatCurrencyBySymbol(calculatedCash, CurrencySymbolEnum.USD)}`}
      </span>
    </div>
  );

  const renderCurrencyExchange = () => (
    <div className={styles.currencyExchangeWrapper}>
      <span className={styles.exchangeText}>
        {lang.cryptoPayoutExchangeRate(
          CurrencyEnum.USD,
          CryptocurrenciesEnum.USDT,
          cryptoRate ?? 0,
        )}
      </span>
    </div>
  );

  const calculateExchangeValue = (amount: string) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount) || !cryptoRate || !numericAmount) return ZERO_VALUE;
    return formatNumber(numericAmount / cryptoRate, false, false, false, 6);
  };
  const defaultPlaceHolder = CurrencySymbolEnum.USD + ZERO_VALUE;
  const isConfirmDisabled = !dataValues.amountValue
    || Number(dataValues.amountValue) === 0
    || Number(dataValues.amountValue) < fixToFloat(minimumCryptoWithdrawal)
    || Number(dataValues.amountValue) > fixToFloat(calculatedCash)
    || isOpenCryptoWallets;

  return (
    <div className={styles.wrapperContainer}>
      <LogoAndCloseButton
        displayCloseButton={!isMobile}
        displayBackButton={isDefaultGoBackButton}
        onBackBtnPress={() => navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods)}
        onClose={() => navigate(UI_ROUTES.payouts)}
        newWebGoBackButtonDesign
      />
      {!isDefaultGoBackButton && !isMobile && (
        <div className={styles.goBackButtonContainer}>
          <ContainerBackButton
            onBackBtnPress={() => navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods)}
          />
        </div>
      )}
      {isMobile && (
        <div className={styles.mobileBackContainer}>
          <GoBackButton
            onBackBtnPress={() => navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods)}
          />
        </div>
      )}
      <div className={styles.containerContent}>
        {isMobile ? (
          <>
            <div className={styles.wrapperContentMobile}>
              <h3 className={styles.title}>{lang.mobileWithdrawScreenTitle()}</h3>
              <span className={styles.description}>
                {lang.mobileWithdrawCryptoScreenSubtitle()}
              </span>
              <div className={styles.mobileFormGroup}>
                <div className={styles.bankTextMobile}>
                  {lang.mobileWithdrawScreenCryptoWallet()}
                </div>
                <CommonDropdown
                  options={cryptoWalletOptions}
                  selectedOption={{
                    id: String(dataValues.selectedAccount?.id ?? ''),
                    label: renderWalletName(walletAddress),
                  }}
                  onSelectOption={handleSelectCryptoWallet}
                  isOpen={isOpenCryptoWallets}
                  setIsOpen={setIsOpenCryptoWallets}
                  title={lang.payoutsCrypto()}
                  footer={cryptoWalletsFooter}
                />
                <div className={styles.currencyAmountWrapper}>
                  <div className={styles.amountWrapper}>
                    <div className={styles.amountTextMobile}>{lang.mobileDepositAmountLabel()}</div>
                    <div
                      className={classNames(styles.currencyInputWrapper, {
                        [styles.hasError]: hasAmountError,
                      })}
                    >
                      <CurrencyInput
                        value={dataValues.amountValue}
                        onValueChange={(value) => {
                          setDataValues({ ...dataValues, amountValue: value as string });
                        }}
                        className={classNames(styles.input, {
                          [styles.errorInput]: hasAmountError,
                        })}
                        placeholder={defaultPlaceHolder}
                        allowNegativeValue={false}
                        prefix={CurrencySymbolEnum.USD}
                      />
                      <span className={styles.inExchangeValue}>
                        {`${lang.commonIn()} ${CryptocurrenciesEnum.USDT} ${calculateExchangeValue(dataValues.amountValue)}`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.amountAndExchangeWrapper}>
                  {renderAvailableAmount()}
                  {renderCurrencyExchange()}
                </div>
              </div>
            </div>
            <div className={styles.confirmAndFeeMobileWrapper}>
              <Button
                className={classNames(styles.buttonRequest, {
                  [styles.buttonRequestDisabled]: isConfirmDisabled,
                })}
                fullWidth
                disabled={isConfirmDisabled}
                onClick={handleRequestWithdrawal}
              >
                {lang.mobileWithdrawScreenRequestBtn()}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.wrapperContent}>
              <h3 className={styles.title}>{lang.mobileWithdrawScreenTitle()}</h3>
              <span className={styles.description}>
                {lang.mobileWithdrawCryptoScreenSubtitle()}
              </span>
              <div>
                <div className={styles.amountText}>{lang.mobileWithdrawScreenCryptoWallet()}</div>
                <CommonDropdown
                  options={cryptoWalletOptions}
                  selectedOption={{
                    id: String(dataValues.selectedAccount?.id ?? ''),
                    label: renderWalletName(walletAddress),
                  }}
                  onSelectOption={handleSelectCryptoWallet}
                  isOpen={isOpenCryptoWallets}
                  setIsOpen={setIsOpenCryptoWallets}
                  title={lang.payoutsCrypto()}
                  footer={cryptoWalletsFooter}
                />
              </div>
              <div className={styles.wrapperFieldAmount}>
                <div className={styles.currencyAmountWrapper}>
                  <div className={styles.amountWrapper}>
                    <div className={styles.amountText}>{lang.mobileDepositAmountLabel()}</div>
                    <div
                      className={classNames(styles.currencyInputWrapper, {
                        [styles.hasError]: hasAmountError,
                      })}
                    >
                      <CurrencyInput
                        value={dataValues.amountValue}
                        onValueChange={(value) => {
                          setDataValues({ ...dataValues, amountValue: value as string });
                        }}
                        className={classNames(styles.input, {
                          [styles.errorInput]: hasAmountError,
                        })}
                        placeholder={defaultPlaceHolder}
                        allowNegativeValue={false}
                        prefix={CurrencySymbolEnum.USD}
                      />
                      <span className={styles.inExchangeValue}>
                        {`${lang.commonIn()} ${CryptocurrenciesEnum.USDT} ${calculateExchangeValue(dataValues.amountValue)}`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.amountAndExchangeWrapper}>
                  {renderAvailableAmount()}
                  {renderCurrencyExchange()}
                </div>
              </div>
              <Button
                className={classNames(styles.buttonRequest, {
                  [styles.buttonRequestDisabled]: isConfirmDisabled,
                })}
                fullWidth
                disabled={isConfirmDisabled}
                onClick={handleRequestWithdrawal}
              >
                {lang.mobileWithdrawScreenRequestBtn()}
              </Button>
            </div>
          </>
        )}
      </div>
      <BaseModal isOpen={Boolean(openPopoverInfo)}>
        <p className={styles.messageAvailableCashTitle}>
          {lang.mobileWithdrawScreenInfoModalTitle(formatCurrency(calculatedCash))}
        </p>
        <p className={styles.messageAvailableCash}>
          {lang.mobileWithdrawScreenInfoModalDescription()}
        </p>
        <Button fullWidth onClick={() => setOpenPopoverInfo(false)}>
          {lang.commonContinueButton()}
        </Button>
      </BaseModal>

      {isMobile ? (
        <MobileDrawer
          title={lang.walletWithdrawalsRestricted()}
          isOpen={isShowNoActiveAccountModal}
          onClose={() => setIsShowNoActiveAccountModal(false)}
        >
          <div className={styles.drawerText}>
            <p>
              {lang.walletNoActiveAccountPleaseContact()}
              {' '}
              <a
                className={styles.supportLink}
                href={config.links.helpCenterUrl}
                target="_blank"
                rel="noreferrer"
              >
                {lang.commonSupport()}
              </a>
              {'.'}
            </p>
          </div>
          <Button
            className={styles.okBtn}
            fullWidth
            onClick={() => setIsShowNoActiveAccountModal(false)}
          >
            {lang.commonOkay()}
          </Button>
        </MobileDrawer>
      ) : (
        <BaseModal isOpen={isShowNoActiveAccountModal}>
          <Modal
            title={lang.walletWithdrawalsRestricted()}
            descriptionComponent={(
              <div>
                {lang.walletNoActiveAccountPleaseContact()}
                {' '}
                <a href={config.links.helpCenterUrl} target="_blank" rel="noreferrer">
                  {lang.commonSupport()}
                </a>
              </div>
            )}
          />
        </BaseModal>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  cryptoRate: state.payment.cryptoRate?.rate,
  tradingAccounts: state.payment.tradingAccounts,
  cryptoLinkedAccounts: state.payment.cryptoLinkedAccounts,
  availableCash: state.payment.availableCashInfo.availableCash.map(
    (available) => available.totalInBaseCurrency.availableCash,
  ),
  payoutSplit: getCurrentAccountTradingPlan(state.crm)?.payout_split,
  accountStatusData: state.closeout.accountStatusData,
  currentPlan: getCurrentAccountTradingPlan(state.crm),
  buyingPower: getCurrentAccountTradingPlan(state.crm)?.daily_hard_bp,
  referenceIds: state.crm.cashAccounts,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  sendWithdrawalRequest: (data: WithdrawalRequest) =>
    dispatch(registerCryptoWithdrawalRequest(data)),
  getAvailablePayout: (payload: GetAllAvailableCashPayload) =>
    dispatch(getAccountsAvailableCash(payload)),
  fetchCryptoRate: () => dispatch(getCryptoRate()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = ConnectedProps<typeof connector>;

export default connector(WithdrawCryptoAmount);
